import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Users, CheckCircle, XCircle, Building2, Shield } from 'lucide-react';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { Badge } from '../../components/common/Badge';
import { userApi } from '../../services/adminApi';

interface User {
  id: number;
  email: string;
  full_name?: string;
  picture?: string;
  is_active: boolean;
  is_admin: boolean;
  can_access_square: boolean;
  can_access_ai: boolean;
  organizations?: Array<{
    id: string;
    name: string;
    display_name?: string;
    role?: string;
  }>;
  organization?: {
    id: string;
    name: string;
    display_name?: string;
  };
  created_at: string;
  last_login?: string;
}

interface UserListProps {
  onNavigate: (page: string, id?: string) => void;
}

export const UserList: React.FC<UserListProps> = ({ onNavigate }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userApi.list();
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch users');
      }
      
      setUsers(response.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number, email: string) => {
    if (!confirm(`Are you sure you want to delete user "${email}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      const response = await userApi.delete(id.toString());

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete user');
      }

      await fetchUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      const response = await userApi.update(user.id.toString(), {
        is_active: !user.is_active
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to update user');
      }

      await fetchUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  const handleToggleAdmin = async (user: User) => {
    try {
      const response = await userApi.update(user.id.toString(), {
        is_admin: !user.is_admin
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to update user');
      }

      await fetchUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={fetchUsers} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-600 mt-1">Manage all user accounts and permissions</p>
        </div>
        <Button
          onClick={() => onNavigate('admin-user-add')}
          icon={Plus}
          variant="primary"
        >
          Add User
        </Button>
      </div>

      <div className="grid gap-4">
        {users.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No users</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating a new user.</p>
              <div className="mt-6">
                <Button
                  onClick={() => onNavigate('admin-user-add')}
                  icon={Plus}
                  variant="primary"
                >
                  Add User
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organization
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Permissions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {user.picture ? (
                            <img
                              className="h-10 w-10 rounded-full"
                              src={user.picture}
                              alt={user.full_name || user.email}
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-gray-600 font-medium">
                                {(user.full_name || user.email).charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.full_name || user.email}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(user.organizations && user.organizations.length > 0) || user.organization ? (
                          <div className="flex items-center gap-1">
                            <Building2 className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-900">
                              {user.organizations && user.organizations.length > 0 
                                ? (user.organizations[0].display_name || user.organizations[0].name)
                                : user.organization 
                                  ? (user.organization.display_name || user.organization.name)
                                  : 'No organization'}
                            </span>
                            {user.organizations && user.organizations[0]?.role && (
                              <Badge variant="secondary">
                                {user.organizations[0].role}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">No organization</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-1">
                          {user.is_admin && (
                            <Badge variant="error">Admin</Badge>
                          )}
                          {user.can_access_square && (
                            <Badge variant="success">Square</Badge>
                          )}
                          {user.can_access_ai && (
                            <Badge variant="warning">AI</Badge>
                          )}
                          {!user.is_admin && !user.can_access_square && !user.can_access_ai && (
                            <Badge variant="secondary">Basic</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleActive(user)}
                          className="flex items-center gap-1 cursor-pointer"
                        >
                          {user.is_active ? (
                            <>
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <span className="text-sm text-gray-900">Active</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="h-4 w-4 text-red-500" />
                              <span className="text-sm text-gray-900">Inactive</span>
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.last_login ? (
                          new Date(user.last_login).toLocaleDateString()
                        ) : (
                          'Never'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleToggleAdmin(user)}
                            className={`p-1 rounded ${
                              user.is_admin
                                ? 'text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50'
                                : 'text-gray-400 hover:text-gray-500 hover:bg-gray-50'
                            }`}
                            title={user.is_admin ? 'Remove admin' : 'Make admin'}
                          >
                            <Shield className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => onNavigate('admin-user-edit', user.id.toString())}
                            className="text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 p-1 rounded"
                            title="Edit user"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(user.id, user.email)}
                            className="text-red-600 hover:text-red-900 hover:bg-red-50 p-1 rounded"
                            title="Delete user"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};